import {
  type ApolloSubscribeResponse,
  defaultCheckboxes,
} from '@/components/SignupForm/use-signup-form';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

const EMAIL_STORAGE_KEY = 'pw_report_gate_email';
const postHogReportEmailGateEvent = 'pw_landing_page_report_email_gate';
const postHogReportEmailEvent = 'pw_landing_page_report_email';
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useReport({ requireEmailGate = true } = {}) {
  const postHog = usePostHog();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [gateOpen, setGateOpen] = useState(false);
  const [email, setEmail] = useState(
    () => localStorage.getItem(EMAIL_STORAGE_KEY) || ''
  );
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEmailPending, setIsEmailPending] = useState(false);

  const hasEmail = useMemo(() => emailRegex.test(email), [email]);

  const pdfUrl = import.meta.env.VITE_REPORT_PDF_URL || '/report.pdf';

  const totalPages = numPages ?? 1;
  const canGoPrev = pageNumber > 1;
  const canGoNext = pageNumber < totalPages;

  const requestNextPage = useCallback(() => {
    if (!canGoNext) return;
    const next = pageNumber + 1;

    if (requireEmailGate && next > 3 && !hasEmail) {
      setGateOpen(true);
      return;
    }

    setPageNumber(next);
  }, [canGoNext, pageNumber, hasEmail, requireEmailGate]);

  const requestPrevPage = useCallback(() => {
    if (!canGoPrev) return;
    setPageNumber(p => Math.max(1, p - 1));
  }, [canGoPrev]);

  const handleSubmitEmail = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = email.trim();

      if (!trimmed) {
        setEmailError('Please enter your email');
        return;
      }
      if (!emailRegex.test(trimmed)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      setIsEmailPending(true);
      setEmailError(null);

      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        const response = await fetch(`${apiUrl}/apollo/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: trimmed,
            mailingList: { ...defaultCheckboxes, report: true },
          }),
        });

        const data = (await response.json()) as ApolloSubscribeResponse & {
          error?: string;
        };

        if (!response.ok) {
          setEmailError(data.error || 'Failed to subscribe');
          return;
        }

        if (postHog) {
          postHog.capture(
            requireEmailGate
              ? postHogReportEmailGateEvent
              : postHogReportEmailEvent
          );
        }

        localStorage.setItem(EMAIL_STORAGE_KEY, trimmed);
        setEmail(trimmed);
        setGateOpen(false);
      } catch {
        setEmailError('Something went wrong. Please try again.');
      } finally {
        setIsEmailPending(false);
      }
    },
    [email, postHog, requireEmailGate]
  );

  const handleChangeEmail = useCallback((next: string) => {
    setEmail(next);
    setEmailError(null);
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return {
    pdfUrl,
    numPages,
    setNumPages,
    pageNumber,
    canGoPrev,
    canGoNext,
    requestPrevPage,
    requestNextPage,
    gateOpen,
    setGateOpen,
    email,
    emailError,
    isEmailPending,
    handleChangeEmail,
    handleSubmitEmail,
  };
}
